import { Button } from '@/components/ui/Button'
import type { ProjectRecord } from '@/data/useProjects'
import { formatDate } from '@/lib/format'

interface AssignedProjectListProps {
  title: string
  projects: ProjectRecord[]
  showCompletion?: boolean
  onOpen: (project: ProjectRecord) => void
}

/** Ongoing / Completed project list for supplier & client (spec §5.2, §6.2). */
export function AssignedProjectList({
  title,
  projects,
  showCompletion = false,
  onOpen,
}: AssignedProjectListProps) {
  return (
    <section>
      <h2 className="mb-2 text-lg font-semibold text-slate-700">{title}</h2>
      {projects.length === 0 ? (
        <p className="text-sm text-slate-400">No {title.toLowerCase()} projects.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-slate-200 text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Project</th>
                <th className="px-4 py-3 font-medium">Start date</th>
                <th className="px-4 py-3 font-medium">Product</th>
                <th className="px-4 py-3 font-medium">Quantity</th>
                <th className="px-4 py-3 font-medium">Delivery</th>
                {showCompletion && <th className="px-4 py-3 font-medium">Completed</th>}
                {showCompletion && <th className="px-4 py-3 font-medium">Duration</th>}
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-800">{project.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                    {formatDate(project.startDate)}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{project.productName}</td>
                  <td className="px-4 py-3 text-slate-600">{project.productQuantity}</td>
                  <td className="px-4 py-3 text-slate-600">{project.deliveryLocation}</td>
                  {showCompletion && (
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                      {formatDate(project.completionDate)}
                    </td>
                  )}
                  {showCompletion && (
                    <td className="px-4 py-3 text-slate-600">
                      {project.durationDays ?? '—'}
                      {project.durationDays != null ? ' days' : ''}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <Button variant="secondary" onClick={() => onOpen(project)}>
                        Open
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
